import { api, LONG_WRITE_TIMEOUT_MS } from '../lib/api.js';
import type {
  BulkMissedCheckoutInput,
  MissedCheckoutPreviewInput,
  MissedCheckoutQueueQueryInput,
  MissedCheckoutSkip,
} from '@attendance/shared';

/** One person's open check-in on the day being fixed. */
export interface MissedCheckoutVisit {
  checkInId: string;
  employeeId: string;
  checkedInAt: string;
  worksiteName: string | null;
  /** The check-out an earlier save wrote that is still in force — a save at another time RETIMES it; null when none. */
  checkOutAt: string | null;
}

export interface MissedCheckoutPreview {
  date: string;
  /** The pre-entered check-out, HH:MM; null only with no open check-in. */
  suggestedTime: string | null;
  /** SHIFT: the roster's most common shift end; CHECK_IN_PLUS: no shift end, so the latest check-in + 9h. */
  suggestedFrom: 'SHIFT' | 'CHECK_IN_PLUS' | null;
  visits: MissedCheckoutVisit[];
  /** Selected people with nothing to fix, and why. */
  skipped: Array<{ employeeId: string; reason: MissedCheckoutSkip }>;
}

export interface MissedCheckoutResult {
  checkInId: string;
  /** Null when the check-in was not found (or is outside what the caller may see). */
  employeeId: string | null;
  /**
   * fixed — written, and the day left the unclosed rule. saved_still_open —
   * written, but the recomputed day is STILL unclosed (still in Missing
   * punches): never reported as done. skipped — nothing written, see `reason`.
   */
  outcome: 'fixed' | 'saved_still_open' | 'skipped';
  reason: MissedCheckoutSkip | null;
}

/** One past day checked into and never closed — a row of the missing check-out queue. */
export interface MissedCheckoutQueueRow {
  employeeId: string;
  name: string;
  employeeCode: string;
  /** The company day, YYYY-MM-DD. */
  date: string;
  /** The day's first check-in; null when the rollup recorded none. */
  checkedInAt: string | null;
  /** The check-in still open — the one a check-out closes; null when none is. */
  openCheckInAt: string | null;
  /** The site of the open check-in, else of the first; null when it carried none. */
  worksiteName: string | null;
  /** A day with no check-in: the time "Set check-in time" pre-enters — shift start, else check-out − 9h. */
  suggestedCheckInTime: string | null;
  suggestedCheckInFrom: 'SHIFT' | 'CHECK_OUT_MINUS' | null;
}

export interface MissedCheckoutQueuePage {
  rows: MissedCheckoutQueueRow[];
  /** Every matching day, not this page's length — the pager and the badge read it. */
  total: number;
  page: number;
  pageSize: number;
}

/* Under ['corrections'], so a bulk save — which invalidates that root — moves
   the queue and its badge with it. */
export const MISSED_CHECKOUT_QUEUE_KEY = ['corrections', 'missed-checkouts', 'queue'] as const;
export const MISSED_CHECKOUT_COUNT_KEY = ['corrections', 'missed-checkouts', 'count'] as const;

export const missedCheckoutsApi = {
  preview: (input: MissedCheckoutPreviewInput) =>
    api
      .post<{ data: MissedCheckoutPreview }>('/corrections/missed-checkouts/preview', input)
      .then((r) => r.data),
  apply: (input: BulkMissedCheckoutInput) =>
    api
      // Up to 200 days, each a correction and a recompute: past the 30s default (F-12).
      .post<{ data: MissedCheckoutResult[] }>('/corrections/missed-checkouts', input, { timeout: LONG_WRITE_TIMEOUT_MS })
      .then((r) => r.data),
  queue: (query: MissedCheckoutQueueQueryInput) => {
    const params = new URLSearchParams();
    if (query.employeeId) params.set('employeeId', query.employeeId);
    if (query.date) params.set('date', query.date);
    if (query.search?.trim()) params.set('search', query.search.trim());
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('pageSize', String(query.pageSize));
    const qs = params.toString();
    return api
      .get<{ data: MissedCheckoutQueuePage }>(`/corrections/missed-checkouts${qs ? `?${qs}` : ''}`)
      .then((r) => r.data);
  },
  count: () =>
    api.get<{ data: { count: number } }>('/corrections/missed-checkouts/count').then((r) => r.data.count),
};
